import React, { useEffect, useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Select from "react-select";
import TextField from "@mui/material/TextField";

import { createStyles, makeStyles } from "@mui/styles";

import Binance from "binance-api-node";
import {
  CoinInfoDetail,
  DepositDetail,
  NetworkListDetail,
} from "../../types/types";
import { COINS_INFO_URL, USER_ASSETS_URL } from "../../constants/urls";
import {
  CURRENTLY_SUPPORTED_NETWORK,
  DEFAULT_WITHDRAW_COIN,
} from "../../constants/constants";

const useStyles = makeStyles((theme) =>
  createStyles({
    input: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: "rgba(0, 0, 0, 0.23)", // default
        },
        "&.Mui-focused fieldset": {
          border: "2px solid red", // focus
        },
      },
    },
  })
);
const options = [
  { value: "BTC", label: "BTC", symbol: "BTC", name: "Bitcoin" },
  { value: "USDT", label: "USDT", symbol: "USDT", name: "TetherUS" },
  { value: "ETH", label: "ETH", symbol: "ETH", name: "Ethereum" },
];

const OptionLayout = (props) => {
  const { innerProps, innerRef } = props;
  return (
    <article
      ref={innerRef}
      {...innerProps}
      className={"custom-option flex hover:bg-backgroundGray py-[10px]"}
    >
      <div className="ml-[10px] mr-[20px]">{props.data.coin}</div>
      <div className={"sub text-normalGray"}>{props.data.name} </div>
    </article>
  );
};

const networkOptions = [
  "Bitcoin",
  "BNB Beacon Chain (BEP2)",
  "BNB Smart Chain (BEP20)",
  "Ethereum (ERC20)",
  "Lightning Network",
  "BTC(SegWit)",
];

const WithdrawContent = () => {
  const [activeNetworkIdx, setActiveNetworkIdx] = useState(0);
  const [tokenOptions, setTokenOptions] = useState<CoinInfoDetail[]>([]);
  const [chosenOption, setChosenOption] = useState<CoinInfoDetail | null>();
  const [receipientAddress, setRecepientAddress] = useState<string>("");
  const [withdrawInputAmount, setWithdrawInputAmount] = useState<string>("");

  const classes = useStyles();

  const fetchCoinsInfo = async () => {
    const req = await fetch(COINS_INFO_URL);
    const data: CoinInfoDetail[] = await req.json();

    const filteredData = data.filter((tokenInfo) => {
      if (tokenInfo.depositAllEnable) {
        const filteredNetwork = filterOptions(tokenInfo.networkList);
        return filteredNetwork.length > 0;
      } else {
        return false;
      }
    });

    const newFilretedData = filteredData.map((data) => {
      const networkList = filterOptions(data.networkList);
      return {
        ...data,
        networkList: networkList,
        name: data.coin,
        label: data.coin,
      };
    });

    setTokenOptions(newFilretedData);

    // set BTC as initial deposit or withdraw
    const btcTokenOption = newFilretedData.find((option) => {
      return option.coin === DEFAULT_WITHDRAW_COIN;
    });

    setChosenOption(btcTokenOption);
  };

  const filterOptions = (options: NetworkListDetail[]): NetworkListDetail[] => {
    return options.filter((option) =>
      CURRENTLY_SUPPORTED_NETWORK.includes(option.network)
    );
  };

  const fetchUserAsset = async () => {
    const resp = await fetch(USER_ASSETS_URL);
    const data = await resp.json();
  };

  const handleTokenOptionChange = (tokenOption: CoinInfoDetail | null) => {
    setChosenOption(tokenOption);
  };

  useEffect(() => {
    fetchCoinsInfo();
  }, []);

  const withdrawFee =
    parseFloat(
      chosenOption?.networkList[activeNetworkIdx].withdrawFee ?? "0"
    ) ?? 0;

  let total =
    parseFloat(chosenOption?.free ?? "0") +
    parseFloat(chosenOption?.locked ?? "0");

  return (
    <div className="w-full px-[32px] py-[43px]">
      <div className="text-3xl font-medium mb-[30px]">Withdraw</div>
      <div className="mb-[20px]">
        <div className="font-semibold text-sm">Crypto Address</div>
        <div className="border-b-[2px] border-brightGreen mt-[6px] w-[105px]"></div>
        <div className="border-b-[1px] border-normalBorder mt-[0.5px]"></div>
      </div>

      <div className="flex justify-between">
        <div>
          <div className="text-normalGray mb-[15px] text-sm">Coin</div>
          <div className="w-[550px] mb-[18px]">
            {chosenOption && (
              <Select
                defaultValue={chosenOption}
                options={tokenOptions}
                components={{ Option: OptionLayout }}
                onChange={(tokenOption) => handleTokenOptionChange(tokenOption)}
              />
            )}
          </div>
          <div>
            <div className="w-[240px] flex text-xs mb-[10px]">
              <p className="inline flex-1 text-normalGray">Total:</p>
              <p className="inline flex-1 font-semibold">
                {chosenOption && `${total} ${chosenOption.coin}`}
              </p>
            </div>

            <div className="w-[240px] flex text-xs  mb-[10px]">
              <p className="inline flex-1 text-normalGray">In order:</p>
              <p className="inline flex-1 font-semibold">
                {chosenOption && `${chosenOption.locked} ${chosenOption.coin}`}
              </p>
            </div>

            <div className="w-[240px] flex text-xs">
              <p className="inline flex-1 text-normalGray">
                Available balance:
              </p>
              <p className="inline flex-1 font-semibold">
                {chosenOption && `${chosenOption.free} ${chosenOption.coin}`}
              </p>
            </div>
          </div>
        </div>

        <div
          style={{ boxShadow: "0 0 4px rgba(0,0,0,.2)", borderRadius: 10 }}
          className="px-[20px] py-[20px] w-[460px] h-[550px]"
        >
          <p className="text-sm font-light mb-[5px]">Select network: </p>

          <div className="flex flex-wrap mb-[30px]">
            {chosenOption &&
              chosenOption.networkList.map((network, idx) => {
                return (
                  <div
                    className={`cursor-pointer mb-[10px] mr-[10px] border-[1px] px-[10px] py-[5px] ${
                      activeNetworkIdx === idx
                        ? "border-brightGreen"
                        : "border-borderDarkDray"
                    } rounded-[4px] text-sm`}
                    onClick={() => setActiveNetworkIdx(idx)}
                    key={idx}
                  >
                    {network.name}
                  </div>
                );
              })}
          </div>
          <div className="mb-[40px]">
            <TextField
              className={classes.input + " w-full"}
              id="standard-basic"
              label={`${chosenOption?.coin} Recepient's Address`}
              variant="standard"
              sx={{ placeholder: { color: "red" } }}
              onChange={(e) => setRecepientAddress(e.target.value)}
            />
          </div>
          <div className="mb-[10px]">
            <TextField
              className={classes.input + " w-full"}
              id="standard-basic"
              label="Amount"
              variant="standard"
              sx={{ placeholder: { color: "red" } }}
              onChange={(e) => setWithdrawInputAmount(e.target.value)}
            />
          </div>

          <div>
            <p className="inline text-xs">Available Balance： </p>
            <p className="inline text-brightGreen text-xs">
              {`${0.0123312} ${chosenOption?.coin}`}
            </p>
          </div>

          <div className="mb-[20px]">
            <p className="inline text-xs">Minimum Withdrawal: </p>
            <p className="inline text-xs">{`${chosenOption?.networkList[activeNetworkIdx].withdrawMin} ${chosenOption?.coin}`}</p>
          </div>

          <div>
            <p className="inline text-sm">Transaction Fee: </p>
            <p className="inline text-sm">{`${withdrawFee} ${chosenOption?.coin}`}</p>
          </div>

          <div className="mb-[50px]">
            <p className="inline text-sm">You will get: </p>
            <p className="inline text-sm">{`${
              withdrawInputAmount === ""
                ? 0
                : parseFloat(withdrawInputAmount) - withdrawFee
            } ${chosenOption?.coin}`}</p>
          </div>

          <div
            style={{
              background: "linear-gradient(90deg,#2a9662 0,#3dcd89 99.77%)",
            }}
            className={`text-sm font-light text-center py-[6px] mt-[30px]`}
          >
            {`Submit`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawContent;
